import { Anchor, Badge, Box, Card, Collapse, Flex, Grid, Text, Tooltip } from '@mantine/core';
import {
  IconAward,
  IconChevronDown,
  IconChevronUp,
  IconCode,
  IconFileText,
  IconHistory,
  IconMail,
  IconPalette,
  IconSearch,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import prettifiedLabel from '../../constants/labelPrettifier';

const TemplatesCard = ({ template }) => {
  const location = useLocation();
  const { accountId, domainId } = useParams();
  const [open, setOpen] = useState(true);
  const { domain } = useSelector((state) => state.domain);

  let badges = [];
  if (template.scope === 'email' && template.emails) {
    badges = Object.values(template.emails).map((email, index) => (
      <Badge key={`email-${index}`} variant="default">
        <IconMail size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/emails/${email.id}/details`} size="xs">
          {email.name}
        </Anchor>
      </Badge>
    ));
  }

  if (template.scope === 'web' && template.recommenders) {
    badges = template.recommenders.map((recommender, index) => (
      <Badge key={`rec-${index}`} variant="default">
        <IconAward size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
        <Anchor
          href={`#/accounts/${accountId}/domains/${domainId}/pages/${recommender.pageId}/recommenders/${recommender.id}/details`}
          size="xs"
        >
          {recommender.page?.name} - {recommender.name}
        </Anchor>
      </Badge>
    ));
  }

  if (template.searches && template.searches.length > 0) {
    badges = [
      ...badges,
      <Badge key="search-as-you-type" variant="default">
        <IconSearch size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
        <Anchor href={`#/accounts/${domain.accountId}/domains/${domain.id}/searchAsYouType`} size="xs">
          Search-as-you-type
        </Anchor>
      </Badge>,
    ];
  }

  if (template.scope === 'search') {
    if (domain.searchBarEnabled && domain.searchBarTemplateId === template.id) {
      badges = [
        <Badge key="search" variant="default">
          <IconSearch size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          <Anchor href={`#/accounts/${accountId}/domains/${domainId}/searchAsYouType`} size="xs">
            Search As You Type
          </Anchor>
        </Badge>,
      ];
    }
  }

  const getScopeIcon = () => {
    switch (template.scope) {
      case 'web':
        return <IconFileText size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />;
      case 'email':
        return <IconMail size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />;
      case 'search':
        return <IconSearch size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />;
      default:
        return null;
    }
  };

  return (
    <Card onClick={() => setOpen(!open)} mb="md" withBorder>
      <Card.Section inheritPadding py="sm">
        <Grid align="center">
          <Grid.Col span={{ base: 12, sm: 10 }}>
            <Anchor href={`#${location.pathname}/${template.id}/details`} fw={600} style={{ display: 'block' }}>
              {template.name}
            </Anchor>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 2 }}>
            <Flex justify="flex-end">{open ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}</Flex>
          </Grid.Col>
        </Grid>
      </Card.Section>
      <Collapse expanded={open}>
        {/* A Card.Section here would be :first-child of the Collapse div and
            get Mantine's negative first/last-child margins, collapsing the
            spacing - a plain Box sits inside the card padding instead. */}
        <Box pt="xs">
          <Flex gap="xs" wrap="wrap">
            <Badge variant="default">
              {getScopeIcon()}
              {prettifiedLabel(template.scope)}
            </Badge>
            <Badge variant="default">
              {template.advancedEditor ? (
                <IconCode size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              ) : (
                <IconPalette size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              )}
              {template.advancedEditor ? 'HTML Editor' : 'Visual Editor'}
            </Badge>
            {badges}
            <Tooltip
              label={
                <>
                  <div>Created: {new Date(template.createdAt).toLocaleString()}</div>
                  <div>Updated: {new Date(template.updatedAt).toLocaleString()}</div>
                </>
              }
              position="top"
              withArrow
            >
              <Badge variant="default" style={{ cursor: 'pointer' }}>
                <IconHistory size={14} style={{ verticalAlign: 'middle' }} />
              </Badge>
            </Tooltip>
          </Flex>
          {badges.length === 0 && (
            <Text size="sm" c="dimmed" mt="xs">
              Not used
            </Text>
          )}
        </Box>
      </Collapse>
    </Card>
  );
};

export default TemplatesCard;
